import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import { createPortal } from "react-dom";

/** Which bookable schedule family this block applies to (UI + future payload). */
export const BLOCK_TIME_SCHEDULE_CLASSES = "classes";
export const BLOCK_TIME_SCHEDULE_PT = "pt";
export const BLOCK_TIME_SCHEDULE_FACILITIES = "facilities";

/** Date scope for when the block applies (maps to API `schedule_type` / calendar fields). */
export const BLOCK_DATE_SINGLE = "single_day";
export const BLOCK_DATE_RANGE = "date_range";

const POPOVER_EST_HEIGHT = 320;
const POPOVER_EST_WIDTH = 296;

/**
 * Map API list items (classes, PT sessions, facilities) to stable `{ id, name }` rows.
 * `raw` is kept for later submit payloads.
 */
export function normalizeSchedulingBookableRows(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((row) => ({
      id: String(row._id ?? row.id ?? ""),
      name: String(row.name ?? row.title ?? "").trim() || "Untitled",
      raw: row,
    }))
    .filter((x) => x.id);
}

function calendarDateToYyyyMmDd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) {
    return "";
  }
  const uy = d.getUTCFullYear();
  const um = d.getUTCMonth();
  const ud = d.getUTCDate();
  const utcMidnightMs = Date.UTC(uy, um, ud);
  const isExactlyUtcMidnight =
    d.getTime() === utcMidnightMs &&
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  if (isExactlyUtcMidnight) {
    return `${uy}-${String(um + 1).padStart(2, "0")}-${String(ud).padStart(2, "0")}`;
  }
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function dateAtNoonFromYyyyMmDd(ymd) {
  if (!ymd || typeof ymd !== "string") return undefined;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

function normalizeCalendarDateForPicker(d) {
  const ymd = calendarDateToYyyyMmDd(d);
  if (!ymd) {
    return d;
  }
  return dateAtNoonFromYyyyMmDd(ymd);
}

function joinEnglishList(items) {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}

function startOfLocalDayMs(d) {
  if (!d || !(d instanceof Date)) return NaN;
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/**
 * Selected bookable Mongo ids (classes, PT sessions, facilities) for POST bodies.
 */
export function getSelectedBookableIds({ rows, selection, allChecked }) {
  if (!rows?.length) return [];
  if (allChecked) return rows.map((r) => r.id);
  return rows.filter((r) => selection[r.id]).map((r) => r.id);
}

/**
 * One POST body per bookable — matches POST /api/scheduling/blackouts `action: "single_event"`.
 */
export function buildBlockTimeBlackoutPayloads({
  bookableIds,
  dateMode,
  singleDayDate,
  rangeStartDate,
  rangeEndDate,
}) {
  const ids = bookableIds.filter(Boolean);
  if (ids.length === 0) return [];

  if (dateMode === BLOCK_DATE_SINGLE) {
    const date = calendarDateToYyyyMmDd(singleDayDate);
    if (!date) return [];
    return ids.map((bookable_event_id) => ({
      action: "single_event",
      bookable_event_id,
      schedule_type: "single_day",
      time_mode: "full_day",
      single_day: { date },
      confirm: true,
    }));
  }

  const start_date = calendarDateToYyyyMmDd(rangeStartDate);
  const end_date = calendarDateToYyyyMmDd(rangeEndDate);
  if (!start_date || !end_date) return [];
  return ids.map((event_id) => ({
    action: "single_event",
    event_id,
    schedule_type: "date_range",
    time_mode: "full_day",
    date_range: { start_date, end_date },
    confirm: true,
  }));
}

/** Scroll target: “Select Date” (when to block). */
export const BLOCK_TIME_SCROLL_SELECT_DATE = "select_date";

/** POST each payload from `buildBlockTimeBlackoutPayloads` — same as Block Time modal submit. */
export async function submitBlockTimePayloads(payloads) {
  if (!Array.isArray(payloads) || payloads.length === 0) return;
  for (const body of payloads) {
    const res = await fetch("/actions/scheduling/blackouts", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    let data = {};
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      throw new Error(
        (typeof data?.error === "string" && data.error) ||
          (typeof data?.message === "string" && data.message) ||
          `Could not create block time (${res.status})`
      );
    }
  }
}

function BlockTimeCalendarCaption(props) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayMonth = props?.displayMonth ?? new Date();
  const monthLabel = displayMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  return (
    <div className="create-class-modal-calendar-caption">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Previous month"
      >
        <img src="/img/new-icons/arrow-left.svg" alt="" />
      </button>
      <span className="create-class-modal-calendar-title">{monthLabel}</span>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Next month"
      >
        <img src="/img/new-icons/arrow-right.svg" alt="" />
      </button>
    </div>
  );
}

function emptySelection(ids) {
  return ids.reduce((acc, id) => {
    acc[id] = false;
    return acc;
  }, {});
}

function BlockTimeCheckboxList({
  allLabel,
  rows,
  selection,
  onToggleAll,
  onToggleOne,
  allChecked,
  emptyMessage,
  listsLoading,
}) {
  if (listsLoading) {
    return (
      <p className="create-block-time-empty" aria-live="polite">
        Loading your schedule…
      </p>
    );
  }
  if (rows.length === 0) {
    return <p className="create-block-time-empty">{emptyMessage}</p>;
  }
  return (
    <ul className="create-block-time-checkbox-list">
      <li>
        <label className="checkbox-input">
          <input type="checkbox" checked={allChecked} onChange={onToggleAll} />
          <span className="checkbox" aria-hidden="true" />
          <span className="text">{allLabel}</span>
        </label>
      </li>
      <li className="create-block-time-checkbox-sep" aria-hidden="true" />
      {rows.map((row) => (
        <li key={row.id}>
          <label className="checkbox-input">
            <input
              type="checkbox"
              checked={Boolean(selection[row.id])}
              onChange={() => onToggleOne(row.id)}
            />
            <span className="checkbox" aria-hidden="true" />
            <span className="text">{row.name}</span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {boolean} [props.listsLoading]
 * @param {unknown[]} [props.classes]
 * @param {unknown[]} [props.ptSessions]
 * @param {unknown[]} [props.facilities]
 * @param {boolean} [props.showFacilities]
 * @param {(payloads: object[]) => Promise<void>} [props.onConfirm] — POST each payload
 * @param {string | null} [props.initialScheduleKind] — `BLOCK_TIME_SCHEDULE_*` when preselecting one bookable
 * @param {string | null} [props.initialPreselectedBookableId]
 * @param {string | null} [props.initialScrollSection] — e.g. `BLOCK_TIME_SCROLL_SELECT_DATE`
 */
const CreateBlockTimeModal = ({
  isOpen,
  onClose,
  listsLoading = false,
  classes = [],
  ptSessions = [],
  facilities = [],
  showFacilities = true,
  onConfirm,
  initialScheduleKind = null,
  initialPreselectedBookableId = null,
  initialScrollSection = null,
}) => {
  const [scheduleKind, setScheduleKind] = useState(BLOCK_TIME_SCHEDULE_CLASSES);

  const [dateMode, setDateMode] = useState(BLOCK_DATE_SINGLE);
  const [singleDayDate, setSingleDayDate] = useState(undefined);
  const [rangeStartDate, setRangeStartDate] = useState(undefined);
  const [rangeEndDate, setRangeEndDate] = useState(undefined);
  const [openDatePicker, setOpenDatePicker] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const singlePickerRef = useRef(null);
  const rangeStartPickerRef = useRef(null);
  const rangeEndPickerRef = useRef(null);
  const calendarPopoverRef = useRef(null);
  const dateSectionRef = useRef(null);
  const [calendarPopoverPos, setCalendarPopoverPos] = useState({
    top: 0,
    left: 0,
  });

  const classRows = useMemo(
    () => normalizeSchedulingBookableRows(classes),
    [classes]
  );
  const ptRows = useMemo(
    () => normalizeSchedulingBookableRows(ptSessions),
    [ptSessions]
  );
  const facilityRows = useMemo(
    () => normalizeSchedulingBookableRows(facilities),
    [facilities]
  );

  const [classSelection, setClassSelection] = useState({});
  const [ptSelection, setPtSelection] = useState({});
  const [facilitySelection, setFacilitySelection] = useState({});

  const getPickerRefForKey = useCallback((key) => {
    switch (key) {
      case "blockSingle":
        return singlePickerRef;
      case "blockRangeStart":
        return rangeStartPickerRef;
      case "blockRangeEnd":
        return rangeEndPickerRef;
      default:
        return null;
    }
  }, []);

  const updateCalendarPopoverPosition = useCallback(() => {
    if (!openDatePicker) return;
    const anchorRef = getPickerRefForKey(openDatePicker);
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const left = Math.max(
      pad,
      Math.min(rect.left, window.innerWidth - POPOVER_EST_WIDTH - pad)
    );
    let top = rect.bottom + gap;
    if (
      rect.bottom + POPOVER_EST_HEIGHT > window.innerHeight - pad &&
      rect.top > POPOVER_EST_HEIGHT + pad
    ) {
      top = rect.top - POPOVER_EST_HEIGHT - gap;
    }
    setCalendarPopoverPos({ top, left });
  }, [openDatePicker, getPickerRefForKey]);

  useLayoutEffect(() => {
    if (!openDatePicker) return;
    updateCalendarPopoverPosition();
  }, [openDatePicker, updateCalendarPopoverPosition]);

  useEffect(() => {
    if (!openDatePicker) return;
    const onScrollOrResize = () => updateCalendarPopoverPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [openDatePicker, updateCalendarPopoverPosition]);

  useEffect(() => {
    if (!openDatePicker) return;
    const handleClickOutside = (e) => {
      const anchorRef = getPickerRefForKey(openDatePicker);
      const inAnchor = anchorRef?.current?.contains(e.target);
      const inPopover = calendarPopoverRef.current?.contains(e.target);
      if (!inAnchor && !inPopover) {
        setOpenDatePicker(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDatePicker, getPickerRefForKey]);

  useEffect(() => {
    if (!isOpen) return;

    const nextClass = emptySelection(classRows.map((c) => c.id));
    const nextPt = emptySelection(ptRows.map((p) => p.id));
    const nextFac = emptySelection(facilityRows.map((f) => f.id));

    if (initialScheduleKind != null) {
      setScheduleKind(initialScheduleKind);
      if (initialPreselectedBookableId != null) {
        const bid = String(initialPreselectedBookableId);
        if (
          initialScheduleKind === BLOCK_TIME_SCHEDULE_CLASSES &&
          Object.hasOwn(nextClass, bid)
        ) {
          nextClass[bid] = true;
        } else if (
          initialScheduleKind === BLOCK_TIME_SCHEDULE_PT &&
          Object.hasOwn(nextPt, bid)
        ) {
          nextPt[bid] = true;
        } else if (
          initialScheduleKind === BLOCK_TIME_SCHEDULE_FACILITIES &&
          Object.hasOwn(nextFac, bid)
        ) {
          nextFac[bid] = true;
        }
      }
    } else {
      setScheduleKind(BLOCK_TIME_SCHEDULE_CLASSES);
    }

    setClassSelection(nextClass);
    setPtSelection(nextPt);
    setFacilitySelection(nextFac);
    setDateMode(BLOCK_DATE_SINGLE);
    setSingleDayDate(undefined);
    setRangeStartDate(undefined);
    setRangeEndDate(undefined);
    setOpenDatePicker(null);
    setSubmitError(null);
    setIsSubmitting(false);
  }, [
    isOpen,
    classRows,
    ptRows,
    facilityRows,
    initialPreselectedBookableId,
    initialScheduleKind,
  ]);

  useLayoutEffect(() => {
    if (!isOpen || initialScrollSection !== BLOCK_TIME_SCROLL_SELECT_DATE) {
      return;
    }
    if (listsLoading) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        dateSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [isOpen, initialScrollSection, listsLoading]);

  const allClassesChecked = useMemo(
    () => classRows.length > 0 && classRows.every((c) => classSelection[c.id]),
    [classRows, classSelection]
  );

  const allPtChecked = useMemo(
    () => ptRows.length > 0 && ptRows.every((p) => ptSelection[p.id]),
    [ptRows, ptSelection]
  );

  const allFacilitiesChecked = useMemo(
    () =>
      facilityRows.length > 0 &&
      facilityRows.every((f) => facilitySelection[f.id]),
    [facilityRows, facilitySelection]
  );

  const activeBookable = useMemo(() => {
    if (scheduleKind === BLOCK_TIME_SCHEDULE_CLASSES) {
      return {
        rows: classRows,
        selection: classSelection,
        allChecked: allClassesChecked,
        allLabel: "all classes",
      };
    }
    if (scheduleKind === BLOCK_TIME_SCHEDULE_PT) {
      return {
        rows: ptRows,
        selection: ptSelection,
        allChecked: allPtChecked,
        allLabel: "all PT sessions",
      };
    }
    return {
      rows: facilityRows,
      selection: facilitySelection,
      allChecked: allFacilitiesChecked,
      allLabel: "all facilities",
    };
  }, [
    scheduleKind,
    classRows,
    classSelection,
    allClassesChecked,
    ptRows,
    ptSelection,
    allPtChecked,
    facilityRows,
    facilitySelection,
    allFacilitiesChecked,
  ]);

  const selectedTargetLabel = useMemo(() => {
    if (listsLoading) return "";
    const { rows, selection, allChecked, allLabel } = activeBookable;
    if (rows.length === 0) return "";
    if (allChecked) return allLabel;
    const names = rows.filter((r) => selection[r.id]).map((r) => r.name);
    if (names.length === 0) return "";
    return joinEnglishList(names);
  }, [listsLoading, activeBookable]);

  const dateSelectionValid = useMemo(() => {
    if (dateMode === BLOCK_DATE_SINGLE) {
      return Boolean(singleDayDate);
    }
    if (!rangeStartDate || !rangeEndDate) return false;
    return startOfLocalDayMs(rangeStartDate) <= startOfLocalDayMs(rangeEndDate);
  }, [dateMode, singleDayDate, rangeStartDate, rangeEndDate]);

  const showSummaryBanner = Boolean(selectedTargetLabel) && dateSelectionValid;

  const formatDate = (d) =>
    d
      ? d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

  const canSubmit =
    !listsLoading && Boolean(selectedTargetLabel) && dateSelectionValid;

  const toggleAllClasses = useCallback(() => {
    const next = !allClassesChecked;
    setClassSelection(Object.fromEntries(classRows.map((c) => [c.id, next])));
  }, [allClassesChecked, classRows]);

  const toggleClass = useCallback((id) => {
    setClassSelection((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAllPt = useCallback(() => {
    const next = !allPtChecked;
    setPtSelection(Object.fromEntries(ptRows.map((p) => [p.id, next])));
  }, [allPtChecked, ptRows]);

  const togglePt = useCallback((id) => {
    setPtSelection((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const toggleAllFacilities = useCallback(() => {
    const next = !allFacilitiesChecked;
    setFacilitySelection(
      Object.fromEntries(facilityRows.map((f) => [f.id, next]))
    );
  }, [allFacilitiesChecked, facilityRows]);

  const toggleFacility = useCallback((id) => {
    setFacilitySelection((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleClose = useCallback(() => {
    setSubmitError(null);
    onClose?.();
  }, [onClose]);

  const handleBlockTime = useCallback(async () => {
    if (!canSubmit || isSubmitting) return;
    const bookableIds = getSelectedBookableIds(activeBookable);
    const payloads = buildBlockTimeBlackoutPayloads({
      bookableIds,
      dateMode,
      singleDayDate,
      rangeStartDate,
      rangeEndDate,
    });
    if (payloads.length === 0) {
      setSubmitError("Nothing to submit. Check your selections and dates.");
      return;
    }
    if (typeof onConfirm !== "function") {
      setSubmitError("Submit handler is not configured.");
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onConfirm(payloads);
      handleClose();
    } catch (e) {
      setSubmitError(e?.message ?? "Could not create block time.");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    isSubmitting,
    activeBookable,
    dateMode,
    singleDayDate,
    rangeStartDate,
    rangeEndDate,
    onConfirm,
    handleClose,
  ]);

  const calendarSelectedDate =
    openDatePicker === "blockSingle"
      ? singleDayDate
      : openDatePicker === "blockRangeStart"
        ? rangeStartDate
        : openDatePicker === "blockRangeEnd"
          ? rangeEndDate
          : undefined;

  const calendarPortal =
    openDatePicker &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={calendarPopoverRef}
        className="create-class-modal-date-popover create-blackout-date-popover-portal"
        style={{
          position: "fixed",
          top: calendarPopoverPos.top,
          left: calendarPopoverPos.left,
          zIndex: 400,
        }}
      >
        <DayPicker
          mode="single"
          selected={calendarSelectedDate}
          onSelect={(next) => {
            const norm = next ? normalizeCalendarDateForPicker(next) : next;
            if (openDatePicker === "blockSingle") setSingleDayDate(norm);
            else if (openDatePicker === "blockRangeStart")
              setRangeStartDate(norm);
            else if (openDatePicker === "blockRangeEnd") setRangeEndDate(norm);
            setOpenDatePicker(null);
          }}
          defaultMonth={calendarSelectedDate || new Date()}
          showOutsideDays
          components={{ Caption: BlockTimeCalendarCaption }}
          modifiersClassNames={{ selected: "my-selected" }}
        />
      </div>,
      document.body
    );

  const renderDateField = (id, label, date, pickerKey) => (
    <div className="form-group">
      <span className="create-blackout-date-field-caption">{label}</span>
      <div
        className="create-class-modal-date-dropdown create-blackout-date-field create-block-time-date-dropdown"
        ref={getPickerRefForKey(pickerKey)}
      >
        <input type="hidden" name={id} value={calendarDateToYyyyMmDd(date)} />
        <img
          src="/img/new-icons/calendar-ico.svg"
          alt=""
          className="create-class-modal-date-icon"
          aria-hidden
        />
        <button
          type="button"
          id={id}
          className="create-class-modal-date-input create-class-modal-date-trigger create-block-time-date-trigger"
          onClick={() =>
            setOpenDatePicker((prev) => (prev === pickerKey ? null : pickerKey))
          }
          aria-label={label}
          aria-expanded={openDatePicker === pickerKey}
        >
          {formatDate(date) || "Select"}
        </button>
        <svg
          className="create-class-modal-select-chevron"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M6 9l6 6 6-6"
          />
        </svg>
      </div>
    </div>
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="Modal-Box create-class-modal create-blackout-modal create-block-time-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-block-time-modal-title"
    >
      <button
        type="button"
        className="overlay"
        onClick={handleClose}
        aria-label="Close modal"
      />
      <div className="box">
        <header>
          <h2 id="create-block-time-modal-title">Create Block time</h2>
          <button
            type="button"
            onClick={handleClose}
            className="close"
            data-button-type="tertiary"
            data-icon-only="true"
            aria-label="Close"
          >
            <img src="/img/new-icons/red-cross.svg" alt="" />
          </button>
        </header>
        <div className="body">
          <div className="create-class-modal-cols">
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">
                Select Schedule
              </h3>
              <p className="create-class-modal-section-desc">
                Choose which schedule type this block time should apply to.
              </p>
            </div>
            <div className="create-class-modal-form">
              <fieldset className="create-blackout-schedule-card">
                <legend className="sr-only">Schedule type</legend>

                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-block-time-schedule-kind"
                      value={BLOCK_TIME_SCHEDULE_CLASSES}
                      checked={scheduleKind === BLOCK_TIME_SCHEDULE_CLASSES}
                      onChange={() =>
                        setScheduleKind(BLOCK_TIME_SCHEDULE_CLASSES)
                      }
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Classes
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings for selected classes.
                      </span>
                    </span>
                  </label>
                  {scheduleKind === BLOCK_TIME_SCHEDULE_CLASSES && (
                    <div className="create-blackout-schedule-nested">
                      <div className="create-block-time-nested-panel">
                        <h4 className="create-block-time-nested-panel-title">
                          Select Classes
                        </h4>
                        <p className="create-block-time-nested-panel-desc">
                          Choose one or more classes to block.
                        </p>
                        <BlockTimeCheckboxList
                          allLabel="All Classes"
                          rows={classRows}
                          selection={classSelection}
                          onToggleAll={toggleAllClasses}
                          onToggleOne={toggleClass}
                          allChecked={allClassesChecked}
                          listsLoading={listsLoading}
                          emptyMessage="No classes on your schedule yet."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-block-time-schedule-kind"
                      value={BLOCK_TIME_SCHEDULE_PT}
                      checked={scheduleKind === BLOCK_TIME_SCHEDULE_PT}
                      onChange={() => setScheduleKind(BLOCK_TIME_SCHEDULE_PT)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        PT Sessions
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block personal training session bookings.
                      </span>
                    </span>
                  </label>
                  {scheduleKind === BLOCK_TIME_SCHEDULE_PT && (
                    <div className="create-blackout-schedule-nested">
                      <div className="create-block-time-nested-panel">
                        <h4 className="create-block-time-nested-panel-title">
                          Select PT Sessions
                        </h4>
                        <p className="create-block-time-nested-panel-desc">
                          Choose one or more PT sessions to block.
                        </p>
                        <BlockTimeCheckboxList
                          allLabel="All PT Sessions"
                          rows={ptRows}
                          selection={ptSelection}
                          onToggleAll={toggleAllPt}
                          onToggleOne={togglePt}
                          allChecked={allPtChecked}
                          listsLoading={listsLoading}
                          emptyMessage="No PT sessions on your schedule yet."
                        />
                      </div>
                    </div>
                  )}
                </div>

                {showFacilities && (
                  <div className="create-blackout-schedule-option">
                    <label className="create-blackout-schedule-option-row radio-input">
                      <input
                        type="radio"
                        name="create-block-time-schedule-kind"
                        value={BLOCK_TIME_SCHEDULE_FACILITIES}
                        checked={
                          scheduleKind === BLOCK_TIME_SCHEDULE_FACILITIES
                        }
                        onChange={() =>
                          setScheduleKind(BLOCK_TIME_SCHEDULE_FACILITIES)
                        }
                      />
                      <span className="radio" aria-hidden="true" />
                      <span className="create-blackout-schedule-option-copy">
                        <span className="create-blackout-schedule-option-title">
                          Facilities
                        </span>
                        <span className="create-blackout-schedule-option-desc">
                          Block bookings for selected facilities.
                        </span>
                      </span>
                    </label>
                    {scheduleKind === BLOCK_TIME_SCHEDULE_FACILITIES && (
                      <div className="create-blackout-schedule-nested">
                        <div className="create-block-time-nested-panel">
                          <h4 className="create-block-time-nested-panel-title">
                            Select Facilities
                          </h4>
                          <p className="create-block-time-nested-panel-desc">
                            Choose one or more facilities to block.
                          </p>
                          <BlockTimeCheckboxList
                            allLabel="All Facilities"
                            rows={facilityRows}
                            selection={facilitySelection}
                            onToggleAll={toggleAllFacilities}
                            onToggleOne={toggleFacility}
                            allChecked={allFacilitiesChecked}
                            listsLoading={listsLoading}
                            emptyMessage="No facilities on your schedule yet."
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </fieldset>
            </div>
          </div>

          <hr className="create-class-modal-separator" />

          <div
            ref={dateSectionRef}
            id="create-block-time-select-date"
            className="create-class-modal-cols"
          >
            <div className="create-class-modal-section-head">
              <h3 className="create-class-modal-section-title">Select Date</h3>
              <p className="create-class-modal-section-desc">
                Choose whether this block applies to a single date or multiple
                dates.
              </p>
            </div>
            <div className="create-class-modal-form">
              <fieldset className="create-blackout-schedule-card">
                <legend className="sr-only">Date scope</legend>
                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-block-time-date-mode"
                      value={BLOCK_DATE_SINGLE}
                      checked={dateMode === BLOCK_DATE_SINGLE}
                      onChange={() => setDateMode(BLOCK_DATE_SINGLE)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Single Day
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings for one specific date.
                      </span>
                    </span>
                  </label>
                  {dateMode === BLOCK_DATE_SINGLE && (
                    <div className="create-blackout-schedule-nested">
                      {renderDateField(
                        "create-block-time-single-date",
                        "Select Date",
                        singleDayDate,
                        "blockSingle"
                      )}
                    </div>
                  )}
                </div>
                <div className="create-blackout-schedule-option">
                  <label className="create-blackout-schedule-option-row radio-input">
                    <input
                      type="radio"
                      name="create-block-time-date-mode"
                      value={BLOCK_DATE_RANGE}
                      checked={dateMode === BLOCK_DATE_RANGE}
                      onChange={() => setDateMode(BLOCK_DATE_RANGE)}
                    />
                    <span className="radio" aria-hidden="true" />
                    <span className="create-blackout-schedule-option-copy">
                      <span className="create-blackout-schedule-option-title">
                        Date Range
                      </span>
                      <span className="create-blackout-schedule-option-desc">
                        Block bookings across multiple consecutive dates.
                      </span>
                    </span>
                  </label>
                  {dateMode === BLOCK_DATE_RANGE && (
                    <div className="create-blackout-schedule-nested">
                      <div className="create-class-modal-form-two-cols">
                        {renderDateField(
                          "create-block-time-range-start",
                          "Start date",
                          rangeStartDate,
                          "blockRangeStart"
                        )}
                        {renderDateField(
                          "create-block-time-range-end",
                          "End date",
                          rangeEndDate,
                          "blockRangeEnd"
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </fieldset>
            </div>
          </div>

          {showSummaryBanner && (
            <div
              className="create-blackout-summary-warning create-block-time-date-banner"
              aria-live="polite"
            >
              <img
                src="/img/new-icons/info-circle-red.svg"
                alt=""
                className="create-blackout-summary-warning-icon"
                aria-hidden
              />
              <p className="create-blackout-summary-warning-text">
                This block will prevent bookings for{" "}
                <strong className="create-blackout-summary-strong">
                  {selectedTargetLabel}
                </strong>
                {dateMode === BLOCK_DATE_SINGLE ? (
                  <>
                    {" "}
                    on{" "}
                    <strong className="create-blackout-summary-strong">
                      {formatDate(singleDayDate)}
                    </strong>
                    .
                  </>
                ) : (
                  <>
                    {" "}
                    from{" "}
                    <strong className="create-blackout-summary-strong">
                      {formatDate(rangeStartDate)}
                    </strong>{" "}
                    to{" "}
                    <strong className="create-blackout-summary-strong">
                      {formatDate(rangeEndDate)}
                    </strong>
                    .
                  </>
                )}
              </p>
            </div>
          )}

          {submitError && (
            <p className="create-class-modal-error" role="alert">
              {submitError}
            </p>
          )}

          <footer className="scheduling-classes-modal-footer">
            <button
              type="button"
              data-button-type="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-button-type="primary"
              onClick={handleBlockTime}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? "Blocking…" : "Block Time"}
            </button>
          </footer>
        </div>
      </div>
      {calendarPortal}
    </div>
  );
};

export default CreateBlockTimeModal;
